// Benefit catalogue — display names, icons, and the static annual value ranges.
// Ranges are transcribed exactly from front-end/pwa/src/config/benefitsData.ts
// (2026/27 rates). Per brief §7.6 these are STATIC ranges, never computed from a
// member's answers — the UI must always label them as estimates.

import { B } from '@/benefits-flow/engine/rules';
import type { ActionState } from '@/benefits-flow/engine/types';

export type ApplyStub = {
  // Honest form structure for an APPLY_NOW destination (a stub, not a real form).
  questionCount: number;
  sections: string[];
  estimate: string;
  note?: string;
};

export type BenefitInfo = {
  id: string;
  // Full name as it appears in benefitsData.ts.
  name: string;
  // Short name for chips and dense lists.
  shortName: string;
  icon: string;
  // Static annual value range in GBP, or null for non-cash benefits.
  min: number | null;
  max: number | null;
  // The exact savingsMessage string from source (already an estimate phrasing).
  savingsMessage: string;
  // The action an eligible result resolves to by default. CA and Blue Badge are
  // overridden dynamically in action.ts (BLOCKED / CHECK_AWARD / route).
  baseAction: Exclude<ActionState, 'MORE_INFO_NEEDED'>;
  // Present only for benefits we hold a form for (APPLY_NOW).
  applyStub?: ApplyStub;
  // True for the two prototype-only benefits, so the UI can flag them.
  prototypeOnly?: boolean;
};

export const BENEFITS: Record<string, BenefitInfo> = {
  [B.attendanceAllowance]: {
    id: B.attendanceAllowance,
    name: 'Attendance Allowance',
    shortName: 'Attendance Allowance',
    icon: 'hand-holding-heart',
    min: 3988.4,
    max: 5959.2,
    savingsMessage: 'Up to £5,959 per year',
    baseAction: 'APPLY_NOW',
    applyStub: {
      // Real structure from the seed JSON (api/domain/care_form/seed/
      // question-json/attendance-allowance/): 64 top-level questions across 6
      // sections, Care Needs dominating. The brief's "65 questions / 287
      // sub-fields / 173 in care" doesn't reconcile with source — code wins
      // (brief §5), so these are the honest figures.
      questionCount: 64,
      sections: [
        'About you',
        'Signing for someone else',
        'Medication and treatments',
        'Care needs',
        'Paying you',
        'Payment and declaration',
      ],
      estimate:
        'About 30–40 minutes. Keni fills most of it from what you give.',
      note: 'Care needs is by far the largest section. Form version gap: the checked-in PDF template is AA1 09/23; the live form is AA1 11/25 v7.0 — needs its own ticket.',
    },
  },
  [B.universalCredit]: {
    id: B.universalCredit,
    name: 'Universal Credit',
    shortName: 'Universal Credit',
    icon: 'money-bills',
    min: 4062.96,
    max: 8003.64,
    savingsMessage: 'Up to £8,004 per year (standard allowance only)',
    baseAction: 'GUIDED',
  },
  [B.pensionCredit]: {
    id: B.pensionCredit,
    name: 'Pension Credit',
    shortName: 'Pension Credit',
    icon: 'sterling-sign',
    min: 0,
    max: 18889,
    savingsMessage: 'Up to £18,889 per year (couple, if no other income)',
    baseAction: 'APPLY_NOW',
    applyStub: {
      questionCount: 40,
      sections: ['About you', 'Income and savings', 'Housing', 'Payment'],
      estimate:
        'About 20 minutes. Precise figures from the screener carry over.',
    },
  },
  [B.pip]: {
    id: B.pip,
    name: 'Personal Independence Payment (PIP)',
    shortName: 'PIP',
    icon: 'wheelchair',
    min: 0,
    max: 10119.2,
    savingsMessage: 'Up to £10,119 per year',
    baseAction: 'APPLY_NOW',
    applyStub: {
      questionCount: 50,
      sections: ['About you', 'Daily living', 'Moving around', 'Extra support'],
      estimate: 'About 45 minutes. A health assessment usually follows.',
    },
  },
  [B.adp]: {
    id: B.adp,
    name: 'Adult Disability Payment (Scotland)',
    shortName: 'Adult Disability Payment',
    icon: 'wheelchair',
    min: 0,
    max: 10119.2,
    savingsMessage: 'Up to £10,119 per year',
    baseAction: 'GUIDED',
  },
  [B.councilTaxReduction]: {
    id: B.councilTaxReduction,
    name: 'Council Tax Reduction',
    shortName: 'Council Tax Reduction',
    icon: 'house',
    min: null,
    max: null,
    savingsMessage: 'Up to 100% off your Council Tax bill',
    baseAction: 'GUIDED',
  },
  [B.nhsFundedNursingCare]: {
    id: B.nhsFundedNursingCare,
    name: 'NHS-funded Nursing Care',
    shortName: 'NHS-funded Nursing Care',
    icon: 'user-nurse',
    min: 13919.36,
    max: 19148.48,
    savingsMessage: 'Up to £19,148 per year towards nursing care',
    baseAction: 'GUIDED',
  },
  [B.freePersonalCare]: {
    id: B.freePersonalCare,
    name: 'Free Personal Care (Scotland)',
    shortName: 'Free Personal Care',
    icon: 'hands-holding-heart',
    min: null,
    max: null,
    savingsMessage:
      'Free personal care services (value depends on your care plan)',
    baseAction: 'GUIDED',
  },
  [B.freeNursingCare]: {
    id: B.freeNursingCare,
    name: 'Free Nursing Care (Scotland)',
    shortName: 'Free Nursing Care',
    icon: 'hands-holding-heart',
    min: null,
    max: null,
    savingsMessage: 'Free nursing care (currently £114.55/week towards costs)',
    baseAction: 'GUIDED',
  },
  [B.disabledFacilitiesGrant]: {
    id: B.disabledFacilitiesGrant,
    name: 'Disabled Facilities Grant',
    shortName: 'Disabled Facilities Grant',
    icon: 'house-chimney-medical',
    min: 0,
    max: 36000,
    savingsMessage: 'Up to £36,000 for home adaptations',
    baseAction: 'GUIDED',
  },
  [B.localAuthoritySocialCare]: {
    id: B.localAuthoritySocialCare,
    name: 'Local Authority Social Care',
    shortName: 'LA Social Care',
    icon: 'building',
    min: null,
    max: null,
    savingsMessage: 'Council-funded care support (value depends on assessment)',
    baseAction: 'GUIDED',
  },
  [B.busPassEngland]: {
    id: B.busPassEngland,
    name: 'Concessionary Bus Pass (England)',
    shortName: 'Bus Pass',
    icon: 'bus',
    min: null,
    max: null,
    savingsMessage: 'Free bus travel (potentially hundreds per year)',
    baseAction: 'INFO_ONLY',
  },
  [B.busPassWales]: {
    id: B.busPassWales,
    name: 'Concessionary Bus Pass (Wales)',
    shortName: 'Bus Pass',
    icon: 'bus',
    min: null,
    max: null,
    savingsMessage: 'Free bus travel (potentially hundreds per year)',
    baseAction: 'INFO_ONLY',
  },
  [B.busPassScotland]: {
    id: B.busPassScotland,
    name: 'Concessionary Bus Pass (Scotland)',
    shortName: 'Bus Pass',
    icon: 'bus',
    min: null,
    max: null,
    savingsMessage: 'Free bus travel (potentially hundreds per year)',
    baseAction: 'INFO_ONLY',
  },
  [B.busPassNorthernIreland]: {
    id: B.busPassNorthernIreland,
    name: 'Concessionary Bus Pass (Northern Ireland)',
    shortName: 'Bus Pass',
    icon: 'bus',
    min: null,
    max: null,
    savingsMessage: 'Free or discounted travel (potentially hundreds per year)',
    baseAction: 'INFO_ONLY',
  },
  // ── Prototype-only ──
  [B.carersAllowance]: {
    id: B.carersAllowance,
    name: "Carer's Allowance",
    shortName: "Carer's Allowance",
    icon: 'hands-holding-heart',
    // ~£86.45/week (2026/27). Single figure, shown as a rounded estimate.
    min: 4495,
    max: 4495,
    savingsMessage: 'Around £4,495 per year (£86.45 per week)',
    baseAction: 'APPLY_NOW',
    prototypeOnly: true,
    applyStub: {
      questionCount: 25,
      sections: [
        'About you',
        'About the person you care for',
        'Your care',
        'Payment',
      ],
      estimate: 'About 20 minutes, once a qualifying award is in place.',
    },
  },
  [B.blueBadge]: {
    id: B.blueBadge,
    name: 'Blue Badge',
    shortName: 'Blue Badge',
    icon: 'square-parking',
    min: null,
    max: null,
    savingsMessage: 'Parking concession (value depends on use)',
    baseAction: 'GUIDED',
    prototypeOnly: true,
  },
};

// Display order for lists/tables that don't re-sort.
export const BENEFIT_ORDER: string[] = Object.keys(BENEFITS);
